<template>
  <div class="api-lookup-auth-section h-100">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div></div>
    <!-- Display the following when no method has been selected by the user. -->
    <div v-if="showBodyConfigMessage" class="centered-div">
      <p>Please select a method to display the Body configuration.</p>
    </div>
    <!-- Displays the following when the selected method does not apply a Body setting. -->
    <div v-if="showMessageForMethodWithoutBody" class="centered-div">
      <p>The Body setting is not applicable for the selected method.</p>
    </div>
    <!-- Display the following when the selected method is valid -->
    <VasionDropList
      v-if="showBodyTypeInput"
      v-model="formFields.bodyType"
      v-slot="slotItem"
      :dataArray="lists.bodyTypes"
      :required="true"
      :showIfEmpty="true"
      :showSearchField="true"
      @input="bodyFormTypeChanged(formFields.bodyType.value==='1'); emitSectionIsDirty();"
      displayName="label"
      title="Body Type"
      type="plain-list"
      valueName="value"
      width="300"
    >
      {{ slotItem.item.label }}
    </VasionDropList>
    <!-- If Body Type is Text, JSON, HTML or XML display the following section. -->
    <div v-if="showTextBox" class="text-box-section">
      <VasionCustomizableTextBox
        ref="bodyContent"
        v-model="formFields.bodyContent"
        :initialLines="10"
        @input="emitSectionIsDirty"
      >
        <template v-slot:customActions>
          <slot name="customActions"></slot>
        </template>
      </VasionCustomizableTextBox>
    </div>
    <div v-if="showFormData" class="form-data-section">
      <ApiLookupFormData
        ref="apiLookupFormData"
        :configurationData="editableData"
        :formType="formType"
        @markAsDirty="emitFormDataSectionIsDirty"
      />
    </div>
  </div>
</template>

<script>
import ApiLookupFormData from '@/components/forms/api/ApiLookupFormData.vue'
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import VasionCustomizableTextBox from '@/components/shared/VasionCustomizableTextBox.vue'

export default {
  name: 'ApiLookupBodyFormSection',
  components: {
    ApiLookupFormData,
    Loading,
    VasionCustomizableTextBox,
  },
  props: {
    configurationData: {
      type: Object,
      required: true,
    },
    method: {
      type: String,
      required: true,
    },
    bodyFormTypeChanged: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      formFields: {
        bodyType: 'Text',
        bodyContent: '\n'.repeat(10),
        bodyFormData: [],
      },
      editableData: this.configurationData,
      isLoading: false,
      lists: {
        bodyTypes: [
          { label: 'None', value: '0' },
          { label: 'Multipart Form', value: '2' },
          { label: 'Form Url Encoded', value: '6' },
          { label: 'Text', value: '3' },
          { label: 'JSON', value: '1' },
          { label: 'HTML', value: '5' },
          { label: 'XML', value: '4' },
        ],
      },
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      requestMethod: this.method,
    }
  },
  watch: {
    configurationData: {
      immediate: true,
      handler(val) {
        if (val && val?.ApiRequestBodyType) {
          this.editableData = val
          this.setBodyFields(val)
        }
      },
    },
    method: {
      immediate: true,
      handler(val) {
        this.requestMethod = val
      },
    },
  },
  computed: {
    formType() {
      return this.formFields.bodyType.value
    },
    showBodyConfigMessage() {
      return this.requestMethod === '' ? true : false
    },
    showBodyTypeInput() {
      return ['POST', 'PATCH', 'PUT'].includes(this.requestMethod)
    },
    showFormData() {
      return (
        this.showBodyTypeInput &&
        (this.formFields.bodyType?.label === 'Multipart Form' || this.formFields.bodyType?.label === 'Form Url Encoded')
      )
    },
    showMessageForMethodWithoutBody() {
      return ['GET', 'DELETE'].includes(this.requestMethod)
    },
    showTextBox() {
      return (
        this.showBodyTypeInput &&
        ['Text', 'JSON', 'HTML', 'XML'].includes(
          this.formFields.bodyType?.label
        )
      )
    },
  },
  async created() {
    this.isLoading = true

    const defaultBodyType = 'None'
    this.formFields.bodyType = this.lists.bodyTypes.find(
      item => item.label === defaultBodyType
    )

    if (this.editableData && this.editableData?.ApiRequestBodyType)
      this.setBodyFields(this.editableData)

    this.bodyFormTypeChanged(this.formFields.bodyType.value==='1')

    this.isLoading = false
  },
  methods: {
    emitFormDataSectionIsDirty(data) {
      this.formFields.bodyFormData = data
      this.emitSectionIsDirty()
    },
    emitSectionIsDirty() {
      let formType = this.formFields.bodyType?.label || ''
      if (formType === 'Multipart Form') formType = 'Form'
      else if (formType === 'Form Url Encoded') {
        formType = 'FormWWWUrlEncoded'
        this.formFields.bodyFormData.forEach(item => {
          item.FormDataType = 1; // Update FormDataType to 1
        });
      }

      const payload = {
        section: 'body',
        data: {
          ApiRequestBodyType: formType,
          ApiRequestBody: this.formFields.bodyContent || '',
          ApiRequestBodyFormData: this.formFields.bodyFormData || [],
        },
      }
      this.$emit('markAsDirty', payload)
    },
    setBodyFields(data) {
      const bodyType = this.lists.bodyTypes.find(
        item => item.value == data.ApiRequestBodyType
      )
      this.formFields.bodyType = bodyType
      this.formFields.bodyContent = data.ApiRequestBody
      this.formFields.bodyFormData = data.ApiRequestBodyFormData
    },
    setReactiveValue(param, value) {
      if (this.showTextBox) {
        this.formFields.bodyContent = value
        this.emitSectionIsDirty();
        return
      }
      this.$refs.apiLookupFormData.setReactiveValue(param, value)
      this.emitSectionIsDirty();
    },    
  },
}
</script>
<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.api-lookup-auth-section {
  padding: 8px;
}
.text-box-section {
  padding-top: 8px;
  padding-bottom: 8px;
}
</style>
